import React from 'react';
const { Icon, Card, Button } = window;
/* ============================================================
   BOOKER — Tela: Quadro Societário
   Navegador SÓ-LEITURA da pasta QUADRO SOCIETARIO no SharePoint:
   uma pasta por empresa/CNPJ, com os documentos societários dentro.
   Expandir carrega o conteúdo sob demanda; "Ver" abre o arquivo.
   ============================================================ */
var { useState, useEffect, useMemo } = React;

const fmtTam = (n) => {
  if (n == null) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
};

async function abrirArquivo(item) {
  try { await window.Api.abrirSharepoint(item.id); }
  catch (e) { window.avisar(e.message || 'Não foi possível abrir o arquivo.'); }
}

/* Um nó da árvore (pasta OU arquivo). Pasta carrega os filhos sob demanda. */
function No({ item, nivel }) {
  const [aberto, setAberto] = useState(false);
  const [itens, setItens] = useState(null); // null = ainda não carregado
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const recuo = 14 + nivel * 22;

  async function toggle() {
    const novo = !aberto;
    setAberto(novo);
    if (novo && itens === null) {
      setCarregando(true); setErro('');
      try { setItens(await window.Api.apiQuadroSocietarioPasta(item.id)); }
      catch (e) { setErro(e.message || 'Falha ao abrir'); }
      finally { setCarregando(false); }
    }
  }

  if (!item.pasta) {
    // arquivo
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', paddingLeft: recuo, borderTop: '1px solid var(--border)' }}>
        <Icon name="doc" size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.nome}</div>
          {item.tamanho != null && <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{fmtTam(item.tamanho)}</div>}
        </div>
        <Button size="sm" variant="ghost" icon="external" onClick={() => abrirArquivo(item)}>Ver</Button>
      </div>
    );
  }

  // pasta
  return (
    <>
      <div onClick={toggle} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', paddingLeft: recuo, borderTop: '1px solid var(--border)', cursor: 'pointer', transition: 'background .15s' }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <Icon name="chevronR" size={15} style={{ color: 'var(--text-muted)', flexShrink: 0, transform: aberto ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
        <Icon name="folder" size={17} style={{ color: 'var(--booker-orange)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.nome}</div>
        {item.qtd != null && <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>{item.qtd} {item.qtd === 1 ? 'item' : 'itens'}</span>}
      </div>
      {aberto && (
        carregando ? <div style={{ padding: '10px 16px', paddingLeft: recuo + 25, fontSize: 12.5, color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>Carregando…</div>
        : erro ? <div style={{ padding: '10px 16px', paddingLeft: recuo + 25, fontSize: 12.5, color: 'var(--danger)', borderTop: '1px solid var(--border)' }}>{erro}</div>
        : (itens && itens.length) ? itens.map(f => <No key={f.id} item={f} nivel={nivel + 1} />)
        : <div style={{ padding: '10px 16px', paddingLeft: recuo + 25, fontSize: 12.5, color: 'var(--text-faint)', borderTop: '1px solid var(--border)' }}>Pasta vazia — nenhum documento ainda.</div>
      )}
    </>
  );
}

function Societarios() {
  const [estado, setEstado] = useState('carregando'); // carregando | ok | erro
  const [erro, setErro] = useState('');
  const [dados, setDados] = useState({ itens: [], pastaUrl: '', configurado: true });
  const [busca, setBusca] = useState('');

  async function carregar() {
    setEstado('carregando'); setErro('');
    try {
      const d = await window.Api.apiQuadroSocietario();
      setDados({ itens: d.itens || [], pastaUrl: d.pastaUrl || '', configurado: d.configurado !== false, erro: d.erro });
      setEstado('ok');
    } catch (e) { setErro(e.message || 'Falha ao carregar'); setEstado('erro'); }
  }
  useEffect(() => { carregar(); }, []);

  const pastas = useMemo(() => dados.itens.filter(i => i.pasta), [dados.itens]);
  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return q ? pastas.filter(p => (p.nome || '').toLowerCase().includes(q)) : pastas;
  }, [pastas, busca]);

  return (
    <div style={{ animation: 'fadeUp .4s ease both' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-.02em' }}>Quadro Societário</h1>
          <p style={{ margin: '5px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Espelho da pasta QUADRO SOCIETARIO no SharePoint · {estado === 'ok' ? `${pastas.length} empresas` : '…'}</p>
          {dados.pastaUrl && (
            <a href={dados.pastaUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 13, fontWeight: 700, color: 'var(--booker-orange)', textDecoration: 'none' }}>
              <Icon name="external" size={14} /> Abrir a pasta no SharePoint
            </a>
          )}
        </div>
        <Button variant="default" icon="refresh" onClick={carregar}>Atualizar</Button>
      </div>

      {estado === 'carregando' && <Card style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando do SharePoint…</Card>}

      {estado === 'erro' && (
        <Card pad={16} style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--danger-bg)', border: '1px solid var(--danger)' }}>
          <Icon name="alert" size={20} style={{ color: 'var(--danger)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}><div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>Não foi possível carregar</div><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{erro}</div></div>
          <Button variant="default" onClick={carregar}>Tentar de novo</Button>
        </Card>
      )}

      {estado === 'ok' && dados.erro && (
        <Card pad={16} style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14, background: 'var(--warn-bg)', border: '1px solid var(--booker-orange)' }}>
          <Icon name="alert" size={20} style={{ color: 'var(--booker-orange)', flexShrink: 0 }} />
          <div style={{ fontSize: 13, color: 'var(--text)' }}>{dados.erro}</div>
        </Card>
      )}

      {estado === 'ok' && !dados.erro && (
        <>
          <div style={{ position: 'relative', maxWidth: 460, marginBottom: 16 }}>
            <Icon name="search" size={17} style={{ position: 'absolute', left: 13, top: 11, color: 'var(--text-muted)' }} />
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar empresa (razão social ou CNPJ)…"
              style={{ width: '100%', padding: '10px 12px 10px 40px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-strong)', fontSize: 13.5, outline: 'none' }} />
          </div>

          <Card pad={0} style={{ overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              {filtradas.length} de {pastas.length} empresas
            </div>
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {filtradas.map(p => <No key={p.id} item={p} nivel={0} />)}
              {!filtradas.length && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>Nenhuma empresa para a busca.</div>}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

Object.assign(window, { Societarios });
